/**
 * Design Tokens - Single Source of Truth
 * All design decisions are centralized here
 */

export const colors = {
  // Primary brand - African purple
  brand: {
    DEFAULT: '#5D2A86',
    dark: '#4A1F6B',
    light: '#7B3FA6',
    soft: '#F3EDF8',
    hover: '#6B3A94',
    active: '#3D1859',
    gradient: 'linear-gradient(135deg, #5D2A86 0%, #7B3FA6 100%)',
  },
  
  // Secondary - Gold accent (African sunrise)
  gold: {
    DEFAULT: '#D4A843',
    light: '#E8C96E',
    soft: '#FDF8ED',
    dark: '#B8922E',
  },
  
  // Teal accent (growth, stability)
  teal: {
    DEFAULT: '#0D9488',
    light: '#99F6E4',
    soft: '#F0FDFA',
    dark: '#0F766E',
  },
  
  // Neutral palette
  neutral: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
    950: '#0A0A0A',
  },
  
  // Semantic colors
  success: {
    DEFAULT: '#059669',
    light: '#D1FAE5',
    soft: '#ECFDF5',
    dark: '#065F46',
  },
  warning: {
    DEFAULT: '#D97706',
    light: '#FEF3C7',
    soft: '#FFFBEB',
    dark: '#B45309',
  },
  error: {
    DEFAULT: '#E11D48',
    light: '#FECDD3',
    soft: '#FFF1F2',
    dark: '#BE123C',
  },
  info: {
    DEFAULT: '#2563EB',
    light: '#DBEAFE',
    soft: '#EFF6FF',
    dark: '#1E40AF',
  },
  
  // Surface colors
  surface: '#FFFFFF',
  background: '#FAF8F5',
  sidebar: '#F8F6F3',
  ink: '#0F172A',
  muted: '#64748B',
  edge: '#E5E7EB',
};

export const typography = {
  fontFamily: {
    sans: ['Inter', 'ui-sans-serif', 'system-ui', '-apple-system', 'sans-serif'],
    mono: ['JetBrains Mono', 'ui-monospace', 'SFMono-Regular', 'Menlo', 'monospace'],
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  sizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  lineHeights: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

export const spacing = {
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  11: '2.75rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem',
  52: '13rem',
  56: '14rem',
  60: '15rem',
  64: '16rem',
  72: '18rem',
  80: '20rem',
  96: '24rem',
};

export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  DEFAULT: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  '4xl': '2rem',
  full: '9999px',
};

export const shadows = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  DEFAULT: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '2xl': '0 50px 100px -15px rgba(0, 0, 0, 0.3)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  brand: '0 4px 14px 0 rgba(93, 42, 134, 0.25)',
};

export const transitions = {
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  slower: '500ms',
  verySlow: '700ms',
  easing: {
    ease: 'ease',
    in: 'ease-in',
    out: 'ease-out',
    inOut: 'ease-in-out',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  header: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  toast: 1600,
  tooltip: 1700,
  devbar: 1800,
  max: 9999,
};

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

export const container = {
  padding: {
    DEFAULT: '1rem',
    sm: '1.5rem',
    lg: '2rem',
    xl: '2.5rem',
  },
  maxWidth: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
    full: '100%',
  },
};

// Export all tokens as a single object
export const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  container,
};

export default designTokens;